import deepmerge from "deepmerge";

/**
 * Almost verbatim excerpt from the Qwoted Tops WordPress plugin
 */

/**
 * Applies "override" keys in the enriched data by merging them into the main
 * structure.
 *
 * @param {Array<Object>} data - The enriched data with potential "override" keys.
 * @returns {Array<Object>} The data with "override" keys merged and removed.
 */
export function applyOverrides(data) {
  return data.map((item) => {
    if (item.override && typeof item.override === "object") {
      const mergedItem = deepmerge(item, item.override);
      delete mergedItem.override;

      return mergedItem;
    }
    return item;
  });
}

/**
 * Fetches avatar image URLs for a list of companies by their slugs.
 *
 * @param {Object} params - The parameters for the API request.
 * @param {string} params.apiDataUrl - The base URL for fetching company data.
 * @param {Object} params.extraHeaders - Additional headers to include in the request (e.g. auth).
 * @param {Array<string>} params.companiesSlugs - A list of company slugs to fetch avatars for.
 * @returns {Promise<Object>} A promise that resolves to a map of company avatars keyed by slug.
 */
async function fetchCompaniesAvatars({ apiDataUrl, extraHeaders, companiesSlugs }) {
  try {
    // Construct the query string to filter profiles by the provided IDs
    const query = companiesSlugs.map((slug) => slug).join(",");
    const endpoint = `${apiDataUrl}?slug=in.(${query})`;

    const response = await fetch(endpoint, {
      headers: extraHeaders,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch companies details: ${response.statusText}`);
    }

    const details = await response.json();

    // Transform the array of details into an avatar map, keyed by slug
    const avatarsMap = details.reduce((accumulator, company) => {
      return { ...accumulator, [company.slug]: company.profile_image_url };
    }, {});

    return avatarsMap;
  } catch (error) {
    throw new Error(`Error fetching profiles details: ${error.message}`);
  }
}

/**
 * Enriches a list of items with avatar image URLs based on their company slugs.
 *
 * @param {Object} params - Parameters for enriching company data.
 * @param {Array<Object>} params.data - The list of items containing company slugs.
 * @param {string} params.apiDataUrl - The base URL for fetching company data.
 * @param {Object} params.extraHeaders - Additional headers to include in the request (e.g. auth).
 * @returns {Promise<Array<Object>>} The enriched data with company avatars added.
 */
export async function enrichCompanyDataWithAvatars({ data, apiDataUrl, extraHeaders }) {
  // Extract all unique company slugs from the data
  const companiesSlugs = data.map((item) => item.company_slug);

  // Fetch avatar information for the extracted slugs
  const companiesAvatars = await fetchCompaniesAvatars({
    apiDataUrl,
    companiesSlugs,
    extraHeaders,
  });

  // Populate profile data with the fetched avatars
  const entriesWithAvatars = data.map((item) => {
    const companyAvatar = companiesAvatars[item.company_slug] || null;
    return { ...item, company_avatar: companyAvatar };
  });

  // Return entries with overrides applied
  return applyOverrides(entriesWithAvatars);
}
